import React from 'react';
import { Card, Image, Button, Text, Group, Title, Grid, Box, Loader, Alert, Container } from '@mantine/core';
import { useNavigate } from 'react-router-dom';
import { useSadhanaData } from '../hooks/useSadhanaData';
import HeroBanner from '../components/HeroBanner';
import RSVPCard from '../components/RSVPCard';
import '../components/Body.css';

const Home = () => {
  const navigate = useNavigate();

  // Use the Firebase hook for data management
  const {
    sadhanaData,
    loading,
    error,
    formattedDate,
    locationLabel
  } = useSadhanaData();

  const handleLocationClick = () => {
    if (sadhanaData.location && sadhanaData.location !== 'online') {
      const locationMap = {
        'high-park': 'High+Park+Toronto',
        'trinity-bellwoods': 'Trinity+Bellwoods+Park+Toronto',
        'centre-island': 'Centre+Island+Toronto',
        'harbourfront': 'Harbourfront+Centre+Toronto'
      };
      const query = locationMap[sadhanaData.location];
      if (query) {
        window.open(`https://www.google.com/maps/search/${query}`, '_blank');
      }
    }
  };

  return (
    <>
      {/* Hero Banner Section */}
      <HeroBanner />

      {/* Main Content Section */}
      <div className="body-container">
        <div className="content-section">
          <style>
            {`
              .sadhana-info-card {
                max-width: 1000px;
                margin: 0 auto 3rem auto;
                border-radius: 20px;
                overflow: hidden;
                background: linear-gradient(145deg, var(--cream) 0%, var(--linen) 100%);
                border: 3px solid var(--saffron-bright);
                box-shadow: 
                  0 12px 40px rgba(244, 196, 48, 0.25),
                  inset 0 2px 0 rgba(255, 255, 255, 0.8);
                transition: all 0.4s ease;
              }
              
              .sadhana-info-card:hover {
                transform: translateY(-8px);
                box-shadow: 
                  0 24px 56px rgba(244, 196, 48, 0.4),
                  0 0 0 4px rgba(244, 196, 48, 0.6),
                  inset 0 2px 0 rgba(255, 255, 255, 0.9);
              }
              
              .content-image {
                width: 100%;
                height: 300px;
                object-fit: cover;
                border-radius: 16px;
                border: 2px solid var(--saffron-medium);
                box-shadow: 0 8px 24px rgba(244, 196, 48, 0.2);
              }
              
              .content-title {
                color: var(--charcoal);
                font-family: 'Georgia', serif;
                font-weight: 700;
                font-size: 2.2rem;
                margin-bottom: 1rem;
                text-shadow: 1px 1px 2px rgba(244, 196, 48, 0.3);
              }
              
              .content-subtitle {
                color: var(--terracotta-medium);
                font-size: 1.3rem;
                font-weight: 500;
                margin-bottom: 1.5rem;
                font-style: italic;
              }
              
              .content-text {
                color: var(--charcoal);
                font-size: 1.1rem;
                line-height: 1.8;
                margin-bottom: 1rem;
              }
              
              .loading-container {
                display: flex;
                flex-direction: column;
                align-items: center;
                padding: 2rem;
                background: var(--cream);
                border-radius: 16px;
                margin: 2rem 0;
              }
              
              /* Mobile Responsive */
              @media (max-width: 768px) {
                .content-title {
                  font-size: 2.2rem !important;
                }
                
                .content-subtitle {
                  font-size: 1.2rem !important;
                }
                
                .content-text {
                  font-size: 1rem !important;
                }
                
                .content-section {
                  padding: 2rem 1rem;
                }
              }
              
              @media (max-width: 480px) {
                .content-title {
                  font-size: 1.8rem !important;
                }
                
                .content-subtitle {
                  font-size: 1.1rem !important;
                }
              }
            `}
          </style>

          {/* Loading State */}
          {loading && (
            <div className="loading-container">
              <Loader color="var(--saffron-bright)" size="lg" />
              <Text size="lg" c="dimmed" mt="md">Loading Sadhana information...</Text>
            </div>
          )}

          {/* Error State */}
          {error && (
            <Alert
              color="orange"
              size="md"
              mb="xl"
              style={{
                background: 'linear-gradient(145deg, var(--cream) 0%, var(--linen) 100%)',
                border: '2px solid var(--terracotta-light)',
                borderRadius: '12px'
              }}
            >
              <Text size="sm">
                Unable to load from Firebase. Showing offline data.
              </Text>
            </Alert>
          )}

          {/* Main Call to Action Section */}
          <Container size="lg" mb="4rem">
            <Grid justify="center" align="stretch">
              {/* Left Column - Main Content */}
              <Grid.Col span={{ base: 12, md: 7 }}>
                <Box style={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <Title
                    className="content-title"
                    style={{
                      fontSize: '2.8rem',
                      marginBottom: '1.5rem',
                      textAlign: 'left'
                    }}
                  >
                    Amrit Vela Sadhana
                  </Title>
                  <Text
                    className="content-subtitle"
                    style={{
                      fontSize: '1.4rem',
                      marginBottom: '2rem',
                      textAlign: 'left'
                    }}
                  >
                    A 4:00 AM, 2.5 hour journey of Jap Ji, Yoga and Kirtan
                  </Text>
                  
                  <Text
                    className="content-text"
                    style={{
                      fontSize: '1.2rem',
                      marginBottom: '1.5rem',
                      textAlign: 'left'
                    }}
                  >
                    Crown yourself in the Early Hours of the Morning. We follow a Sikh Dharma tradition
                    rising 4 hours before sunrise in the hour known as the Amrit Vela.
                  </Text>
                  
                  <Text
                    className="content-text"
                    style={{
                      fontSize: '1.1rem',
                      marginBottom: '2rem',
                      textAlign: 'left'
                    }}
                  >
                    We meet once a month, either in a Church or on a Ski Hill in Etobicoke.
                    Check above to see which and when.
                  </Text>
                  
                  {/* Call to Action Button */}
                  <Box style={{ textAlign: 'left' }}>
                    <Button
                      size="xl"
                      onClick={() => navigate('/sadhana')}
                      style={{
                        background: 'linear-gradient(135deg, var(--saffron-bright), var(--terracotta-light))',
                        color: 'white',
                        borderRadius: '16px',
                        padding: '16px 32px',
                        fontWeight: 700,
                        fontSize: '1.1rem',
                        boxShadow: '0 8px 24px rgba(244, 196, 48, 0.4)',
                        border: 'none',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.transform = 'translateY(-2px) scale(1.05)';
                        e.target.style.boxShadow = '0 12px 32px rgba(244, 196, 48, 0.6)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'translateY(0) scale(1)';
                        e.target.style.boxShadow = '0 8px 24px rgba(244, 196, 48, 0.4)';
                      }}
                    >
                      Learn More About Our Sadhana
                    </Button>
                  </Box>
                </Box>
              </Grid.Col>
              
              {/* Right Column - Next Sadhana Section */}
              <Grid.Col span={{ base: 12, md: 5 }}>
                <Box style={{ height: '100%', display: 'flex', alignItems: 'center' }}>
                  <RSVPCard />
                </Box>
              </Grid.Col>
            </Grid>
          </Container>

          {/* Our Tradition Section */}
          <Container size="lg">
            <Grid>
              <Grid.Col span={12}>
                <Title
                  order={2}
                  mb="3rem"
                  style={{
                    fontSize: '2.4rem',
                    color: 'white',
                    textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8)',
                    textAlign: 'left',
                    fontWeight: 700
                  }}
                >
                  Our Tradition
                </Title>
              </Grid.Col>

              {/* Text Content Column - Full Width */}
              <Grid.Col span={12}>
                <Box style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  height: '100%',
                  paddingLeft: { base: 0, md: '2rem' }
                }}>
                  <Text
                    size="lg"
                    mb="1.5rem"
                    style={{
                      color: 'white',
                      textShadow: '1px 1px 2px rgba(0, 0, 0, 0.8)',
                      lineHeight: 1.8,
                      fontSize: '1.2rem',
                      textAlign: 'left'
                    }}
                  >
                    <strong style={{
                      color: 'var(--saffron-bright)',
                      textShadow: '2px 2px 4px rgba(0, 0, 0, 0.9)'
                    }}>
                      Guru Dharam Singh
                    </strong> is currently carrying on this tradition of organizing and ensuring successful sadhanas.
                  </Text>
                </Box>
              </Grid.Col>
            </Grid>
          </Container>
        </div>
      </div>
    </>
  );
};

export default Home;